import { Layers } from 'lucide-react';

/**
 * Sélecteur de parcours, en tête de thématique.
 *
 * Certaines thématiques abritent plusieurs concours : « Fonction publique d'État »
 * contient l'Inspecteur DGFiP et le Contrôleur DGFiP, plus un fonds commun de
 * français, mathématiques et culture générale. Ce sélecteur filtre tout ce qui
 * suit, y compris les compteurs du dock.
 *
 * Deux règles tenues ici :
 *  - « Tout afficher » existe toujours, et c'est le choix par défaut ;
 *  - un sujet sans rattachement est COMMUN, donc visible quel que soit le parcours
 *    choisi (le filtrage lui-même vit dans `filtrerParParcours`, plus bas).
 *
 * Le composant ne s'affiche pas quand la thématique n'a pas de parcours, ce qui
 * est le cas de toutes les autres aujourd'hui.
 */
// children : posé à la suite des choix (pastille « Ma date d'écrit »).
export default function ParcoursSwitch({ parcours, actif, onChange, compteur, children = null }) {
    if (!parcours || parcours.length < 2) return null;

    // Mobile : libellés courts (« Tout », « Inspecteur », « Contrôleur ») — on retire les mots portés par plus
    // de la moitié des parcours (« DGFiP », absent de « Contrôleur des douanes »), sans jamais laisser un libellé vide.
    const mots = parcours.map(p => [...new Set(String(p.label || '').split(/\s+/).filter(Boolean).map(m => m.toLowerCase()))]);
    const freq = new Map();
    mots.flat().forEach(m => freq.set(m, (freq.get(m) || 0) + 1));
    const communs = new Set([...freq].filter(([, n]) => n > parcours.length / 2).map(([m]) => m));
    const court = (label) => {
        const reste = String(label || '').split(/\s+/).filter(m => !communs.has(m.toLowerCase())).join(' ');
        return reste || label;
    };
    const choix = [{ slug: '', label: 'Tout afficher', court: 'Tout', aide: null }, ...parcours.map(p => ({ ...p, court: court(p.label) }))];
    const retenu = normaliserParcours(parcours, actif);
    const courant = choix.find(c => c.slug === retenu) || choix[0];

    return (
        <div className="parcours-switch" role="group" aria-label="Parcours">
            <span className="parcours-switch__intro">
                <Layers size={15} aria-hidden="true" />
                Je prépare
            </span>
            <div className="parcours-switch__choix">
                {choix.map(c => (
                    <button
                        key={c.slug || 'tous'}
                        type="button"
                        className={`parcours-switch__btn${c.slug === retenu ? ' is-active' : ''}`}
                        aria-pressed={c.slug === retenu}
                        aria-describedby={c.aide ? `parcours-tip-${c.slug}` : undefined}
                        onClick={() => onChange(c.slug)}
                    >
                        {/* Le titre de la thématique donne déjà le contexte : « DGFiP » est retiré partout dans ce
                            sélecteur (libellé court). L'infobulle ne montre que l'aide, sans répéter le libellé. */}
                        <span className="parcours-switch__long">{c.slug ? c.court : c.label}</span>
                        <span className="parcours-switch__short">{c.court}</span>
                        {compteur && typeof compteur[c.slug || ''] === 'number' && (
                            <span className="parcours-switch__n">{compteur[c.slug || '']}</span>
                        )}
                        {c.aide && <span id={`parcours-tip-${c.slug}`} role="tooltip" className="parcours-switch__tip">{[c.aide, c.badge].filter(Boolean).join('. ')}</span>}
                    </button>
                ))}
            </div>
            {children}
            {/* Badge (« Contenu en cours de construction ») : à la fin de la phrase d aide, pas sur le bouton. */}
            {(courant.aide || courant.badge) && (
                <p className="parcours-switch__aide">
                    {courant.aide}
                    {courant.badge && <span className="parcours-switch__badge">{courant.badge}</span>}
                </p>
            )}
        </div>
    );
}

/**
 * Ramène un choix mémorisé à une valeur qui existe encore.
 *
 * Une préférence survit au renommage ou à la suppression d'un parcours. Sans ce
 * garde-fou, un slug devenu inconnu ne correspondait à aucun rattachement : le
 * filtre ne gardait que les sujets communs, soit 63 sur 139, et aucun bouton ne
 * paraissait actif. On retombe donc sur « Tout afficher », et cette même valeur
 * sert au rendu, aux requêtes et à la préférence.
 */
export function normaliserParcours(parcours, actif) {
    if (!actif || !parcours || parcours.length < 2) return '';
    return parcours.some(p => p.slug === actif) ? actif : '';
}

/**
 * Applique le parcours choisi à l'arbre des catégories.
 *
 * Un sous-sujet est gardé s'il n'a AUCUN rattachement (il est commun) ou s'il
 * porte le parcours choisi. Une catégorie qui se retrouve vide disparaît, sans
 * quoi la page afficherait des matières sans contenu.
 *
 * Sans parcours, ou sur « Tout afficher », l'arbre est rendu tel quel : aucune
 * copie, aucun changement de comportement pour les thématiques à un seul concours.
 */
export function filtrerParParcours(categories, parcours, actif) {
    const retenu = normaliserParcours(parcours, actif);
    if (!retenu) return categories;
    return (categories || [])
        .map(cat => ({
            ...cat,
            sous_sujets: (cat.sous_sujets || []).filter(ss => {
                const tags = ss.parcours || [];
                return tags.length === 0 || tags.includes(retenu);
            }),
        }))
        .filter(cat => cat.sous_sujets.length > 0);
}

/** Nombre de sujets visibles pour chaque choix, « Tout afficher » compris. */
export function compterParParcours(categories, parcours) {
    const n = { '': 0 };
    (parcours || []).forEach(p => { n[p.slug] = 0; });
    (categories || []).forEach(cat => (cat.sous_sujets || []).forEach(ss => {
        n[''] += 1;
        const tags = ss.parcours || [];
        (parcours || []).forEach(p => {
            if (tags.length === 0 || tags.includes(p.slug)) n[p.slug] += 1;
        });
    }));
    return n;
}
